/**
 * PSP/CRE Tool v2.0 - Climate Zones & Endemic Disease Configuration
 * 
 * Purpose: Define climate zones and endemic disease risks affecting DNBI rates.
 * DNBI (Disease and Non-Battle Injury) typically accounts for 35%+ of total
 * casualties in military operations.
 * 
 * Author: Dr. Tatar Radu MD
 * Institution: Romanian Military Medical Institute
 * Date: December 21, 2024
 * Version: 2.2 - BUG-068: THEATER_REGIONS dnbi_modifier added + calculateDNBIRate wired (March 9, 2026)
 *   ROOT CAUSE: THEATER_REGIONS entries had battle_casualty_scalar only — no dnbi_modifier.
 *   calculateDNBIRate() never read theater_region from climateConfig.
 *   FIX: Added dnbi_modifier to all 7 THEATER_REGIONS entries (evidence-based, cited below).
 *   FIX: calculateDNBIRate() now reads theater_region and applies theater_dnbi_modifier.
 *   Sources: Blood CG et al. Mil Med 1994 (DNBI comparison Okinawa/Korea/Vietnam/Falklands);
 *   US Army AMEDD FST Handbook 2014 Table 1-1; Norwegian Armed Forces FCI study 2023;
 *   MSMR Cold Weather Injuries 2022-2023; OEF/OIF DNBI comparison (Mil Med 2008);
 *   Ukraine 2022-2024 DNBI key informant interviews (Mil Med 2024).
 * Version: 2.1 - ENH-012: THEATER_REGIONS geographic scalars + getTheaterScalar() (March 7, 2026)
 *   Added THEATER_REGIONS constant: battle casualty rate scalars per CREstT theater type.
 *   Seven regions (CENTRAL_EUROPE baseline=1.0 through URBAN=1.35) with seasonal sub-scalars,
 *   doctrinal source, and planning notes. CENTRAL_EUROPE and null both return 1.0 (backward compat).
 *   Added getTheaterScalar(theater_region, season) exported function consumed by casualtyCalculator.js.
 *   DEFAULT_CLIMATE_CONFIG extended with theater_region: 'CENTRAL_EUROPE'.
 *   Tab2 UI: new Theater Region dropdown in climate Collapse section.
 *
 * Version: 2.0 - Enhanced with icons.js integration
 * 
 * References:
 * - NATO AMedP-1.5 Casualty Estimation
 * - FM 4-02.17 Preventive Medicine Services
 * - USCENTCOM historical DNBI data
 * - WHO endemic disease classifications
 * - Ukraine 2022-2024 DNBI patterns
 * 
 * Usage:
 * import { 
 *   CLIMATE_ZONES,
 *   ENDEMIC_DISEASE_LEVELS,
 *   calculateDNBIRate,
 *   getClimateModifiers
 * } from '../constants/climateZones';
 */

import { ICONS, STATUS_ICONS, WEATHER_ICONS } from '../utils/icons';

// ============================================================================
// CLIMATE ZONES
// ============================================================================

export const CLIMATE_ZONES = {
  TEMPERATE: {
    value: 'TEMPERATE',
    label: 'Temperate',
    shortLabel: 'TEMP',
    description: 'Moderate climate (Europe, Northern US)',
    icon: WEATHER_ICONS.PARTLY_CLOUDY,
    color: 'success',
    
    temperature: {
      summer_high_c: 25,
      winter_low_c: -5
    },
    
    dnbi_base_modifier: 1.0,
    heat_casualty_risk: 0.05,
    cold_casualty_risk: 0.10,
    
    disease_risks: {
      malaria: 0,
      dengue: 0,
      leishmaniasis: 0,
      respiratory: 0.15,
      gi_illness: 0.10,
      vector_borne: 0.05
    },
    
    seasonal_dnbi_modifier: {
      spring: 1.0,
      summer: 1.1,
      fall: 1.0,
      winter: 1.2
    },
    
    ukraine_data: {
      applicable: true,
      notes: 'Eastern Ukraine temperate with harsh winters',
      winter_specific_risks: ['Trench foot', 'Hypothermia', 'Frostbite'],
      rasputitsa_impact: 'Spring/fall mud seasons significantly impact operations'
    },
    
    notes: 'Baseline DNBI rates. Winter respiratory illness increases.'
  },
  
  TROPICAL_WET: {
    value: 'TROPICAL_WET',
    label: 'Tropical Wet',
    shortLabel: 'TROP-W',
    description: 'Hot, humid year-round (Southeast Asia, Central Africa)',
    icon: ICONS.WEATHER.RAIN,
    color: 'warning',
    
    temperature: {
      summer_high_c: 35,
      winter_low_c: 25,
      humidity_percent: 85
    },
    
    dnbi_base_modifier: 1.0,   // 2026-10-13 climate table (DERIVED); was the old value, see docs/research/RESEARCH_CLIMATE.md part 2
    heat_casualty_risk: 0.25,
    cold_casualty_risk: 0,
    
    disease_risks: {
      malaria: 0.35,
      dengue: 0.25,
      leishmaniasis: 0.10,
      respiratory: 0.20,
      gi_illness: 0.35,
      vector_borne: 0.40
    },
    
    seasonal_dnbi_modifier: { dry_season: 1.2, wet_season: 1.3 },
    
    acclimatization_days: 14,
    water_purification_critical: true,
    
    notes: 'High DNBI environment. Vector control critical. Acclimatization essential.'
  },
  
  TROPICAL_DRY: {
    value: 'TROPICAL_DRY',
    label: 'Tropical Dry/Desert',
    shortLabel: 'DESERT',
    description: 'Hot, arid (Middle East, North Africa)',
    icon: WEATHER_ICONS.SUN,
    color: 'warning',
    
    temperature: {
      summer_high_c: 50,
      winter_low_c: 10,
      humidity_percent: 15
    },
    
    dnbi_base_modifier: 1.1,   // 2026-10-13 climate table (EDUCATED GUESS); was the old value, see docs/research/RESEARCH_CLIMATE.md part 2
    heat_casualty_risk: 0.35,
    cold_casualty_risk: 0.05,
    
    disease_risks: {
      malaria: 0.05,
      dengue: 0.05,
      leishmaniasis: 0.25,
      respiratory: 0.15,
      gi_illness: 0.25,
      vector_borne: 0.15
    },
    
    seasonal_dnbi_modifier: { summer: 1.1, winter: 1.0, hot_season: 1.1, mild_season: 1.0 },
    
    acclimatization_days: 10,
    hydration_critical: true,
    
    notes: 'Heat casualties primary risk. Hydration discipline essential.'
  },
  
  ARCTIC: {
    value: 'ARCTIC',
    label: 'Arctic/Subarctic',
    shortLabel: 'ARCTIC',
    description: 'Extreme cold (Northern Europe, Alaska)',
    icon: WEATHER_ICONS.SNOW,
    color: 'info',
    
    temperature: {
      summer_high_c: 15,
      winter_low_c: -40
    },
    
    dnbi_base_modifier: 1.0,   // 2026-10-13 climate table (SOURCED single point: Korea 1951); was the old value, see docs/research/RESEARCH_CLIMATE.md part 2
    heat_casualty_risk: 0,
    cold_casualty_risk: 0.40,
    
    disease_risks: {
      malaria: 0,
      dengue: 0,
      leishmaniasis: 0,
      respiratory: 0.25,
      gi_illness: 0.10,
      vector_borne: 0.02
    },
    
    seasonal_dnbi_modifier: { spring: 1.2, summer: 1.0, fall: 1.2, winter: 1.7 },
    
    cold_weather_injuries: {
      frostbite: 0.20,
      hypothermia: 0.15,
      trench_foot: 0.10
    },
    
    acclimatization_days: 7,
    
    notes: 'Cold injuries primary risk. Proper equipment essential.'
  },
  
  MOUNTAINOUS: {
    value: 'MOUNTAINOUS',
    label: 'High Altitude',
    shortLabel: 'MOUNTAIN',
    description: 'High elevation (>2500m)',
    icon: ICONS.TERRAIN.MOUNTAIN,
    color: 'warning',
    
    altitude_meters: 3000,
    
    dnbi_base_modifier: 1.0,   // 2026-10-13 climate table (SOURCED single point: Korea 1951); was the old value, see docs/research/RESEARCH_CLIMATE.md part 2
    seasonal_dnbi_modifier: { spring: 1.2, summer: 1.0, fall: 1.2, winter: 1.7 },
    heat_casualty_risk: 0.05,
    cold_casualty_risk: 0.25,
    
    disease_risks: {
      malaria: 0,
      dengue: 0,
      leishmaniasis: 0,
      respiratory: 0.30,
      gi_illness: 0.10,
      vector_borne: 0.02
    },
    
    altitude_illnesses: {
      acute_mountain_sickness: 0.25,
      hape: 0.05,
      hace: 0.02
    },
    
    acclimatization_days: 14,
    physical_performance_reduction: 0.25,
    
    notes: 'Altitude acclimatization mandatory. Reduced physical capacity.'
  },
  
  SUBTROPICAL: {
    value: 'SUBTROPICAL',
    label: 'Subtropical',
    shortLabel: 'SUBTROP',
    description: 'Hot summers, mild winters (Mediterranean, Southern US)',
    icon: WEATHER_ICONS.PARTLY_CLOUDY,
    color: 'info',
    
    temperature: {
      summer_high_c: 35,
      winter_low_c: 5
    },
    
    dnbi_base_modifier: 1.1,   // 2026-10-13 climate table (EDUCATED GUESS); was the old value, see docs/research/RESEARCH_CLIMATE.md part 2
    heat_casualty_risk: 0.15,
    cold_casualty_risk: 0.05,
    
    disease_risks: {
      malaria: 0.05,
      dengue: 0.10,
      leishmaniasis: 0.10,
      respiratory: 0.15,
      gi_illness: 0.15,
      vector_borne: 0.15
    },
    
    seasonal_dnbi_modifier: { summer: 1.1, winter: 1.0 },
    
    notes: 'Moderate DNBI risk. Summer heat management important.'
  },
  
  CARPATHIAN: {
    value: 'CARPATHIAN',
    label: 'Carpathian Mountain',
    shortLabel: 'CARP',
    description: 'Romanian mountain climate - cold winters, moderate summers',
    icon: WEATHER_ICONS.SNOW,
    color: 'info',
    
    temperature: {
      summer_high_c: 22,
      winter_low_c: -15
    },
    
    dnbi_base_modifier: 1.0,   // 2026-10-13 climate table (SOURCED single point: Korea 1951); was the old value, see docs/research/RESEARCH_CLIMATE.md part 2
    heat_casualty_risk: 0.05,
    cold_casualty_risk: 0.30,
    
    disease_risks: {
      malaria: 0,
      dengue: 0,
      leishmaniasis: 0,
      respiratory: 0.20,
      gi_illness: 0.10,
      vector_borne: 0.15  // Tick-borne diseases
    },
    
    seasonal_dnbi_modifier: { spring: 1.2, summer: 1.0, fall: 1.2, winter: 1.7 },
    
    cold_weather_injuries: {
      frostbite: 0.15,
      hypothermia: 0.10,
      trench_foot: 0.12
    },
    
    acclimatization_days: 14,
    
    romania_specific: true,
    
    notes: 'Romanian Carpathians. Winter = significant cold injury risk. Tick-borne disease in summer.'
  },
  
  EASTERN_EUROPEAN_WINTER: {
    value: 'EASTERN_EUROPEAN_WINTER',
    label: 'Eastern European Winter',
    shortLabel: 'EE-WINTER',
    description: 'Ukraine/Poland/Romania lowland winter conditions',
    icon: WEATHER_ICONS.SNOW,
    color: 'warning',
    
    temperature: {
      summer_high_c: 30,
      winter_low_c: -20
    },
    
    dnbi_base_modifier: 1.0,   // 2026-10-13 climate table (SOURCED single point: Korea 1951); was the old value, see docs/research/RESEARCH_CLIMATE.md part 2
    heat_casualty_risk: 0.10,
    cold_casualty_risk: 0.35,
    
    disease_risks: {
      malaria: 0,
      dengue: 0,
      leishmaniasis: 0,
      respiratory: 0.25,
      gi_illness: 0.15,
      vector_borne: 0.05
    },
    
    seasonal_dnbi_modifier: { spring: 1.0, summer: 1.0, fall: 1.2, winter: 1.7 },
    
    cold_weather_injuries: {
      frostbite: 0.20,
      hypothermia: 0.15,
      trench_foot: 0.15
    },
    
    trench_conditions: {
      wet_cold_modifier: 1.6,
      trench_foot_risk: 'HIGH',
      respiratory_risk: 'HIGH'
    },
    
    ukraine_validated: true,
    
    ukraine_data: {
      applicable: true,
      notes: 'Ukraine 2022-2024: Winter trench warfare = very high DNBI. Rasputitsa mud seasons impact operations.'
    },
    
    notes: 'Ukraine 2022-2024 validated. Winter trench warfare conditions. High cold injury risk.'
  }
};

// ============================================================================
// ENDEMIC DISEASE RISK LEVELS
// ============================================================================

export const ENDEMIC_DISEASE_LEVELS = {
  NONE: {
    value: 'NONE',
    label: 'No Endemic Disease',
    shortLabel: 'NONE',
    description: 'No significant endemic disease presence',
    icon: STATUS_ICONS.GREEN,
    color: 'success',
    
    dnbi_modifier: 1.0,
    prophylaxis_required: false,
    vector_control_required: false,
    
    notes: 'Standard preventive medicine measures.'
  },
  
  LOW: {
    value: 'LOW',
    label: 'Low Endemic Risk',
    shortLabel: 'LOW',
    description: 'Some endemic disease, low transmission',
    icon: STATUS_ICONS.BLUE,
    color: 'info',
    
    dnbi_modifier: 1.15,
    prophylaxis_required: true,
    vector_control_required: false,
    prophylaxis_coverage_required: 0.80,
    
    notes: 'Prophylaxis recommended. Standard precautions.'
  },
  
  MODERATE: {
    value: 'MODERATE',
    label: 'Moderate Endemic Risk',
    shortLabel: 'MOD',
    description: 'Significant endemic disease presence',
    icon: STATUS_ICONS.YELLOW,
    color: 'warning',
    
    dnbi_modifier: 1.40,
    prophylaxis_required: true,
    vector_control_required: true,
    prophylaxis_coverage_required: 0.95,
    expected_disease_rate_per_1000_month: 25,
    
    notes: 'Mandatory prophylaxis. Active vector control.'
  },
  
  HIGH: {
    value: 'HIGH',
    label: 'High Endemic Risk',
    shortLabel: 'HIGH',
    description: 'High endemic disease transmission',
    icon: STATUS_ICONS.YELLOW_WARNING,
    color: 'error',
    
    dnbi_modifier: 1.80,
    prophylaxis_required: true,
    vector_control_required: true,
    prophylaxis_coverage_required: 1.0,
    expected_disease_rate_per_1000_month: 75,
    hospitalization_rate: 0.15,
    
    notes: 'Intensive disease prevention. Plan for medical surge.'
  },
  
  EPIDEMIC: {
    value: 'EPIDEMIC',
    label: 'Epidemic/Outbreak',
    shortLabel: 'EPIDEMIC',
    description: 'Active disease outbreak in area',
    icon: STATUS_ICONS.RED,
    color: 'error',
    
    dnbi_modifier: 2.5,
    prophylaxis_required: true,
    vector_control_required: true,
    isolation_required: true,
    expected_disease_rate_per_1000_month: 150,
    hospitalization_rate: 0.25,
    
    notes: 'Outbreak conditions. Consider delaying operations.'
  }
};

// ============================================================================
// WATER/FOOD SAFETY
// ============================================================================

export const WATER_FOOD_SAFETY = {
  SAFE: {
    value: 'SAFE',
    label: 'Safe (Developed)',
    shortLabel: 'SAFE',
    description: 'Safe municipal water and food supply',
    icon: STATUS_ICONS.GREEN,
    color: 'success',
    
    gi_illness_modifier: 1.0,
    water_purification_required: false,
    food_inspection_required: false,
    
    notes: 'Standard food/water safety procedures.'
  },
  
  CAUTION: {
    value: 'CAUTION',
    label: 'Caution Required',
    shortLabel: 'CAUTION',
    description: 'Variable water/food safety',
    icon: STATUS_ICONS.YELLOW,
    color: 'warning',
    
    gi_illness_modifier: 1.5,
    water_purification_required: true,
    food_inspection_required: true,
    expected_gi_rate_per_1000_month: 50,
    
    ukraine_data: {
      applicable: true,
      notes: 'Infrastructure damage created water safety issues'
    },
    
    notes: 'Purify all water. Inspect local food sources.'
  },
  
  UNSAFE: {
    value: 'UNSAFE',
    label: 'Unsafe',
    shortLabel: 'UNSAFE',
    description: 'Unsafe local water and food',
    icon: STATUS_ICONS.RED,
    color: 'error',
    
    gi_illness_modifier: 2.5,
    water_purification_required: true,
    food_inspection_required: true,
    local_food_prohibited: true,
    expected_gi_rate_per_1000_month: 200,   // 2026-10-13: first-month diarrhea 150 to 280 per 1,000 (Desert Shield, Thailand, 24th ID); SOURCED range
    
    notes: 'No local food/water. Military supply only.'
  }
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Calculate DNBI rate per 1000 per day
 * @param {Object} climateConfig - Climate configuration
 * @returns {number} DNBI rate
 */
export function calculateDNBIRate(climateConfig) {
  const {
    climate_zone = 'TEMPERATE',
    endemic_disease = 'NONE',
    water_food_safety = 'SAFE',
    season = 'summer',
    acclimatization_complete = true,
    prophylaxis_compliance = 1.0,
    // BUG-068 FIX: read theater_region so its dnbi_modifier is applied to the preview rate
    theater_region = 'CENTRAL_EUROPE'
  } = climateConfig;
  
  const climate = CLIMATE_ZONES[climate_zone] || CLIMATE_ZONES.TEMPERATE;
  const endemic = ENDEMIC_DISEASE_LEVELS[endemic_disease] || ENDEMIC_DISEASE_LEVELS.NONE;
  const waterFood = WATER_FOOD_SAFETY[water_food_safety] || WATER_FOOD_SAFETY.SAFE;
  
  // Base DNBI rate (per 1000 per day) - NATO baseline ~2.0
  let baseDNBI = 2.0;
  
  // Apply climate modifier
  baseDNBI *= climate.dnbi_base_modifier;
  
  // Apply seasonal modifier if available
  const seasonalMod = climate.seasonal_dnbi_modifier?.[season] || 1.0;
  baseDNBI *= seasonalMod;
  
  // Apply endemic disease modifier
  let diseaseMod = endemic.dnbi_modifier;
  if (endemic.prophylaxis_required && prophylaxis_compliance > 0.9) {
    diseaseMod = 1 + (diseaseMod - 1) * (1 - prophylaxis_compliance * 0.7);
  }
  baseDNBI *= diseaseMod;
  
  // Apply water/food modifier (GI is ~30% of DNBI)
  baseDNBI *= (1 + (waterFood.gi_illness_modifier - 1) * 0.3);
  
  // Acclimatization penalty
  if (!acclimatization_complete && climate.acclimatization_days > 0) {
    baseDNBI *= 1.5;
  }

  // BUG-068 FIX: Apply theater-specific DNBI modifier (added to THEATER_REGIONS entries)
  // Models the additional non-battle disease/injury burden beyond climate zone effects.
  // Examples: Arctic +25% cold injuries; Jungle +75% tropical disease; Urban +18% psych/respiratory.
  const theaterDNBIMod = THEATER_REGIONS[theater_region]?.dnbi_modifier ?? 1.0;
  baseDNBI *= theaterDNBIMod;
  
  return Math.round(baseDNBI * 100) / 100;
}

/**
 * Get combined climate/disease modifiers
 * @param {Object} climateConfig - Climate configuration
 * @returns {Object} Combined modifiers and warnings
 */
export function getClimateModifiers(climateConfig) {
  const {
    climate_zone = 'TEMPERATE',
    endemic_disease = 'NONE',
    water_food_safety = 'SAFE'
  } = climateConfig;
  
  const climate = CLIMATE_ZONES[climate_zone] || CLIMATE_ZONES.TEMPERATE;
  const endemic = ENDEMIC_DISEASE_LEVELS[endemic_disease] || ENDEMIC_DISEASE_LEVELS.NONE;
  const waterFood = WATER_FOOD_SAFETY[water_food_safety] || WATER_FOOD_SAFETY.SAFE;
  
  const dnbiRate = calculateDNBIRate(climateConfig);
  
  const warnings = [];
  
  if (climate.acclimatization_days > 7) {
    warnings.push({
      severity: 'info',
      icon: STATUS_ICONS.BLUE,
      category: 'CLIMATE',
      message: `${climate.acclimatization_days} day acclimatization period recommended`
    });
  }
  
  if (climate.heat_casualty_risk > 0.2) {
    warnings.push({
      severity: 'warning',
      icon: STATUS_ICONS.YELLOW_WARNING,
      category: 'HEAT',
      message: 'High heat casualty risk environment'
    });
  }
  
  if (climate.cold_casualty_risk > 0.2) {
    warnings.push({
      severity: 'warning',
      icon: STATUS_ICONS.YELLOW_WARNING,
      category: 'COLD',
      message: 'High cold injury risk environment'
    });
  }
  
  if (endemic.value === 'HIGH' || endemic.value === 'EPIDEMIC') {
    warnings.push({
      severity: 'error',
      icon: STATUS_ICONS.RED,
      category: 'DISEASE',
      message: `${endemic.label} - elevated disease risk`
    });
  }
  
  if (waterFood.value === 'UNSAFE') {
    warnings.push({
      severity: 'warning',
      icon: STATUS_ICONS.YELLOW,
      category: 'WATER',
      message: 'Local water/food unsafe'
    });
  }
  
  return {
    dnbi_rate_per_1000_day: dnbiRate,
    dnbi_modifier: climate.dnbi_base_modifier * endemic.dnbi_modifier,
    heat_casualty_risk: climate.heat_casualty_risk || 0,
    cold_casualty_risk: climate.cold_casualty_risk || 0,
    disease_risk: endemic.dnbi_modifier - 1,
    gi_illness_modifier: waterFood.gi_illness_modifier,
    acclimatization_days: climate.acclimatization_days || 0,
    warnings
  };
}

/**
 * Validate climate configuration
 * @param {Object} climateConfig - Configuration to validate
 * @returns {Object} Validation result
 */
export function validateClimateConfig(climateConfig) {
  const errors = [];
  const warnings = [];
  
  if (!CLIMATE_ZONES[climateConfig.climate_zone]) {
    errors.push('Invalid climate zone');
  }
  
  if (!ENDEMIC_DISEASE_LEVELS[climateConfig.endemic_disease]) {
    errors.push('Invalid endemic disease level');
  }
  
  const endemic = ENDEMIC_DISEASE_LEVELS[climateConfig.endemic_disease];
  if (endemic?.prophylaxis_required && climateConfig.prophylaxis_compliance < 0.8) {
    warnings.push('Prophylaxis compliance below recommended level');
  }
  
  if (endemic?.value === 'EPIDEMIC') {
    warnings.push('Epidemic conditions - consider alternate operations area');
  }
  
  return { 
    errors, 
    warnings, 
    isValid: errors.length === 0,
    icon: errors.length > 0 ? STATUS_ICONS.RED : 
          warnings.length > 0 ? STATUS_ICONS.YELLOW : 
          STATUS_ICONS.GREEN
  };
}

/**
 * Get climate zone options for dropdown
 * @returns {Array} Climate options
 */
export function getClimateZoneOptions() {
  return Object.values(CLIMATE_ZONES).map(zone => ({
    value: zone.value,
    label: zone.label,
    icon: zone.icon,
    dnbi_modifier: zone.dnbi_base_modifier
  }));
}

/**
 * Get endemic disease options for dropdown
 * @returns {Array} Disease level options
 */
export function getEndemicDiseaseOptions() {
  return Object.values(ENDEMIC_DISEASE_LEVELS).map(level => ({
    value: level.value,
    label: level.label,
    icon: level.icon,
    dnbi_modifier: level.dnbi_modifier
  }));
}

// ============================================================================
// ENH-012: THEATER REGIONS — Battle Casualty Rate Scalars
//
// Source: CREstT (Casualty Rate Estimation Tool) theater-level multipliers;
//         NATO MC 326/4 Principles of Medical Support;
//         Dupuy Institute Historical Combat Data (TDI);
//         Ukraine 2022-2024 operational data.
//
// PURPOSE: These scalars adjust the base battle casualty rate for geographic
//          and environmental factors that are independent of operation type.
//          Applied multiplicatively AFTER force superiority adjustment.
//          Distinct from DNBI climate modifiers — these affect BATTLE casualties.
//
// CENTRAL_EUROPE is the NATO planning baseline (scalar = 1.0).
// All other regions are expressed relative to that baseline.
//
// Seasonal sub-scalars capture tempo effects: mud seasons reduce offensive
// operations (fewer casualties), summer enables highest operational tempo.
// ============================================================================

export const THEATER_REGIONS = {
  CENTRAL_EUROPE: {
    value: 'CENTRAL_EUROPE',
    label: 'Central Europe',
    shortLabel: 'C-EUR',
    description: 'NATO primary planning theater (Poland, Germany, Baltics)',
    icon: STATUS_ICONS.BLUE,
    color: 'info',

    // Battle casualty scalar (1.0 = NATO baseline reference)
    battle_casualty_scalar: 1.0,   // 2026-10-13: Dupuy direction (bad weather and difficult terrain lower casualty rates); harsh regions 0.9, EDUCATED GUESS; was the CREstT adjustment (unverified)

    // BUG-068: DNBI theater modifier — additional non-battle disease/injury burden vs. NATO baseline
    // CENTRAL_EUROPE = 1.00 (planning baseline; Bosnia/KFOR: ~8.1/100/week = 1.16/1000/day combat)
    // Source: US Army FST Handbook 2014 Table 1-1; NATO EpiNATO-2 baseline
    dnbi_modifier: 1.0,   // 2026-10-13: the climate zone carries the DNBI environment factor (no stacking)

    // Seasonal sub-scalars on top of the base scalar.
    // Reflect operational tempo changes by season.
    seasonal_battle_modifier: { spring: 0.9, summer: 1.0, fall: 0.92, winter: 0.88 },

    terrain_notes: 'Mixed terrain — plains, forests, urban centres. Road network supports high tempo.',
    doctrine_source: 'NATO MC 326/4; CREstT v3.2 European Theater baseline',
    ukraine_relevance: 'Ukraine 2022-2024 data primarily collected in this environment'
  },

  EASTERN_EUROPE: {
    value: 'EASTERN_EUROPE',
    label: 'Eastern Europe / Ukraine',
    shortLabel: 'E-EUR',
    description: 'Ukraine-pattern conflict: drone saturation, deep fires, contested airspace',
    icon: STATUS_ICONS.YELLOW_WARNING,
    color: 'warning',

    battle_casualty_scalar: 1.0,   // 2026-10-13: Dupuy direction (bad weather and difficult terrain lower casualty rates); harsh regions 0.9, EDUCATED GUESS; was the CREstT adjustment (unverified)

    // BUG-068: DNBI modifier = 1.08 — similar climate to Central Europe but cold injuries,
    // trench exposure, infectious disease and psychiatric burden elevated in Eastern Ukraine pattern.
    // Ukraine 2022-24 key informant study: cold injuries, musculoskeletal, mental health elevated.
    // Source: Ukraine DNBI key informant interviews, Mil Med 2024; Blood CG et al. 1994
    dnbi_modifier: 1.0,   // 2026-10-13: the climate zone carries the DNBI environment factor (no stacking)

    seasonal_battle_modifier: { spring: 0.88, summer: 1.0, fall: 0.9, winter: 0.85 },

    terrain_notes: 'Steppes, river crossings, urban centres. FPV drone density increases fragmentation casualties.',
    doctrine_source: 'Ukraine 2022-2024 OSINT; Kotwal 2016; CREstT Eastern adjustment +8%',
    ukraine_data: {
      applicable: true,
      notes: 'Scalar validated against Ukraine data: drone saturation (+18% fragmentation), artillery dominance (58%)'
    }
  },

  ARCTIC: {
    value: 'ARCTIC',
    label: 'Arctic / Subarctic',
    shortLabel: 'ARCTIC',
    description: 'Northern Europe, Norway, Alaska — extreme cold, limited mobility',
    icon: WEATHER_ICONS.SNOW,
    color: 'info',

    battle_casualty_scalar: 0.9,   // 2026-10-13: Dupuy direction (bad weather and difficult terrain lower casualty rates); harsh regions 0.9, EDUCATED GUESS; was the CREstT adjustment (unverified)

    // BUG-068: DNBI modifier = 1.25 — cold weather injuries add 20-25% DNBI burden.
    // Norwegian Armed Forces FCI study 2023: frostbite up to 20% incidence in arctic manoeuvres.
    // US Army MSMR 2023: cold injuries add significant non-battle DNBI. Hypothermia, trench foot
    // (immersion foot), and frostbite are essentially absent in temperate theater.
    // Blood CG et al. 1994: Falklands (cold/wet) had elevated DNBI vs temperate operations.
    // Source: Norwegian FCI study (Tandfonline 2023); MSMR Cold Weather Injuries 2023;
    //   Blood CG et al. Mil Med 1994; TB MED 508 Arctic operations guidance
    dnbi_modifier: 1.0,   // 2026-10-13: the climate zone carries the DNBI environment factor (no stacking)

    seasonal_battle_modifier: { spring: 0.8, summer: 0.9, fall: 0.85, winter: 0.8 },

    terrain_notes: 'Frozen tundra, taiga. Evacuation severely limited. Cold casualties compound battle losses.',
    doctrine_source: 'ACO Allied Tactical Publication 3.2.1 Arctic Operations; CREstT +12% cold theater'
  },

  DESERT: {
    value: 'DESERT',
    label: 'Desert / Arid',
    shortLabel: 'DESERT',
    description: 'Middle East, North Africa — heat casualties, open terrain, long ranges',
    icon: WEATHER_ICONS.SUN,
    color: 'warning',

    battle_casualty_scalar: 0.9,   // 2026-10-13: Dupuy direction (bad weather and difficult terrain lower casualty rates); harsh regions 0.9, EDUCATED GUESS; was the CREstT adjustment (unverified)

    // BUG-068: DNBI modifier = 1.12 — heat casualties add 10-15% DNBI with modern prevention.
    // Gulf War: heat injuries <1% of DNBI with prevention but GI illness, dermatology elevated.
    // OIF DNBI MEDEVAC rate (Iraq/desert) 40.2/1000 p-yrs vs 56.7/1000 p-yrs Afghanistan.
    // Desert Storm: DNBI admission rates higher than OEF/OIF confirming desert burden.
    // Source: US Army DNBI Model refined OEF/OIF (Mil Med 2008); US Army FST Handbook 2014;
    //   NCBI/NAS Strategies to Protect Health of Deployed US Forces (2000); FM 4-02.17
    dnbi_modifier: 1.0,   // 2026-10-13: the climate zone carries the DNBI environment factor (no stacking)

    seasonal_battle_modifier: { spring: 0.95, summer: 0.9, fall: 0.95, winter: 0.9 },

    terrain_notes: 'Open terrain increases exposure to direct fire. Heat casualties add 15-25% to medical load in summer.',
    doctrine_source: 'OIF/OEF historical data; FM 4-02.17; CREstT +7% desert adjustment'
  },

  JUNGLE_TROPICAL: {
    value: 'JUNGLE_TROPICAL',
    label: 'Jungle / Tropical',
    shortLabel: 'JUNGLE',
    description: 'Southeast Asia, Central Africa — dense terrain, high DNBI, limited air support',
    icon: ICONS.TERRAIN ? ICONS.TERRAIN.FOREST || STATUS_ICONS.GREEN : STATUS_ICONS.GREEN,
    color: 'success',

    battle_casualty_scalar: 0.9,   // 2026-10-13: Dupuy direction (bad weather and difficult terrain lower casualty rates); harsh regions 0.9, EDUCATED GUESS; was the CREstT adjustment (unverified)

    // BUG-068: DNBI modifier = 1.75 — jungle/tropical imposes heaviest DNBI of all environments.
    // Vietnam: disease admissions = 69% of all hospital admissions 1965-69 (AMEDD Center of History).
    // Blood CG et al. 1994: daily DNBI rates up to 4.03/1000 in Vietnam/tropical combat vs
    //   ~0.99-2.0/1000 in temperate theaters; ratio ~2.0 historical. Discounted to 1.75 for modern
    //   prevention (vaccines, prophylaxis, water purification) per NCBI/NAS 2000 review.
    // Source: Blood CG et al. Mil Med 1994; AMEDD Vietnam Medical Support Ch.2;
    //   NCBI/NAS Strategies to Protect Health of Deployed US Forces (2000); FM 3-90.6
    dnbi_modifier: 1.0,   // 2026-10-13: the climate zone carries the DNBI environment factor (no stacking)

    seasonal_battle_modifier: { dry_season: 0.95, wet_season: 0.85 },

    terrain_notes: 'Limited observation, frequent ambush, severely restricted air MEDEVAC. Disease burden compounds.',
    doctrine_source: 'Vietnam DNBI data; FM 3-90.6; CREstT +18% jungle adjustment'
  },

  MOUNTAINOUS: {
    value: 'MOUNTAINOUS',
    label: 'Mountainous / High Altitude',
    shortLabel: 'MOUNT',
    description: 'Caucasus, Hindu Kush, Alps — altitude, terrain friction, limited MEDEVAC',
    icon: ICONS.TERRAIN ? ICONS.TERRAIN.MOUNTAIN || STATUS_ICONS.YELLOW : STATUS_ICONS.YELLOW,
    color: 'warning',

    battle_casualty_scalar: 0.9,   // 2026-10-13: Dupuy direction (bad weather and difficult terrain lower casualty rates); harsh regions 0.9, EDUCATED GUESS; was the CREstT adjustment (unverified)

    // BUG-068: DNBI modifier = 1.25 — altitude + cold + terrain combine for elevated DNBI.
    // OEF (Afghanistan mountainous): DNBI MEDEVAC rate 56.7/1000 p-yrs vs OIF Iraq 40.2/1000 p-yrs
    //   = ratio 1.41 (Mil Med 2008); discounted to 1.25 to separate operational intensity from terrain.
    // Altitude sickness, musculoskeletal injuries from difficult terrain, cold exposure at altitude.
    // Source: OEF/OIF DNBI study (Mil Med 2008); Systematic Review DNBI Mil Med 2024;
    //   Afghanistan CJTF data; altitude physiology (Hackett 2010)
    dnbi_modifier: 1.0,   // 2026-10-13: the climate zone carries the DNBI environment factor (no stacking)

    seasonal_battle_modifier: { spring: 0.85, summer: 0.9, fall: 0.88, winter: 0.85 },

    terrain_notes: 'Altitude impairs physical performance 20-30%. MEDEVAC windows limited. Litter carry significantly longer.',
    doctrine_source: 'Afghanistan CJTF data; CREstT +22% mountain; altitude physiology (Hackett 2010)'
  },

  URBAN: {
    value: 'URBAN',
    label: 'Urban / Mega-City',
    retired: true,   // 2026-10-13: urban is a phase condition, not a theater region
    shortLabel: 'URBAN',
    description: 'Dense urban combat — IED density, short ranges, complex evacuation',
    icon: STATUS_ICONS.RED,
    color: 'error',

    battle_casualty_scalar: 1.0,   // 2026-10-13: Dupuy direction (bad weather and difficult terrain lower casualty rates); harsh regions 0.9, EDUCATED GUESS; was the CREstT adjustment (unverified)

    // BUG-068: DNBI modifier = 1.18 — urban combat elevates psychiatric DNBI and respiratory illness.
    // Rubble dust, sewage exposure, confined spaces increase respiratory infections.
    // Mariupol/Bakhmut 2022-24: prolonged urban siege produces significant psychiatric DNBI
    //   (adjustment disorders, acute stress reactions, combat fatigue beyond baseline).
    // OIF BCT psychiatric DNBI rate: 59.8/1000 combat-years = 23% of total DNBI (J Trauma 2010).
    // Source: BCT DNBI study (J Trauma 2010); Ukraine Mariupol/Bakhmut OSINT; Fallujah/Mosul data
    dnbi_modifier: 1.0,   // 2026-10-13: the climate zone carries the DNBI environment factor (no stacking)

    seasonal_battle_modifier: { spring: 1.0, summer: 1.0, fall: 1.0, winter: 1.0 },

    terrain_notes: 'IED density highest in urban. Blast/fragmentation wounds predominate. Casualty extraction extremely difficult.',
    doctrine_source: 'Fallujah/Mosul data; Ukraine Mariupol/Bakhmut 2022-2024; CREstT +35% MOUT'
  }
};

/**
 * ENH-012: Get the theater scalar for casualty calculation.
 * Returns a number to multiply against base battle casualties.
 *
 * @param {string|null} theaterRegion - Key from THEATER_REGIONS, or null
 * @param {string|null} season - 'spring'|'summer'|'fall'|'winter'|'dry_season'|'wet_season'
 * @returns {number} Scalar ≥ 1.0 (CENTRAL_EUROPE = 1.0; null/unknown = 1.0)
 */
export function getTheaterScalar(theaterRegion, season) {
  if (!theaterRegion) return 1.0;
  const region = THEATER_REGIONS[theaterRegion];
  if (!region) return 1.0;  // Unknown key → baseline, no error

  const baseScalar = region.battle_casualty_scalar ?? 1.0;
  const seasonalMod = region.seasonal_battle_modifier?.[season] || baseScalar;
  // seasonal_battle_modifier already encodes the full scalar at that season
  // (not a relative modifier on base_scalar) — use directly when present
  return seasonalMod;
}

// ============================================================================
// DEFAULT CONFIGURATIONS
// ============================================================================

export const DEFAULT_CLIMATE_CONFIG = {
  enabled: false,
  climate_zone: 'TEMPERATE',
  endemic_disease: 'NONE',
  water_food_safety: 'SAFE',
  season: 'summer',
  hemisphere: 'NORTH',   // R2-09: with an operation start date the season of each day follows the calendar of this hemisphere
  acclimatization_complete: true,
  prophylaxis_compliance: 1.0,
  theater_region: 'CENTRAL_EUROPE'  // ENH-012: default to NATO planning baseline
};

export const EASTERN_EUROPE_CONFIG = {
  enabled: true,
  climate_zone: 'TEMPERATE',
  endemic_disease: 'LOW',
  water_food_safety: 'CAUTION',
  season: 'summer',
  acclimatization_complete: true,
  prophylaxis_compliance: 0.95,
  // BUG-FIX: Apply Eastern Europe button must also set theater region.
  // EASTERN_EUROPE scalar = 1.08 (drone saturation, deep fires, contested airspace).
  theater_region: 'EASTERN_EUROPE'
};

export const UKRAINE_WINTER_CONFIG = {
  enabled: true,
  climate_zone: 'TEMPERATE',
  endemic_disease: 'NONE',
  water_food_safety: 'CAUTION',
  season: 'winter',
  acclimatization_complete: false,
  prophylaxis_compliance: 1.0,
  notes: 'Ukraine winter conditions - cold injuries significant risk'
};

// ============================================================================
// EDUCATIONAL CONTENT
// ============================================================================

export const CLIMATE_EDUCATIONAL = {
  key_principles: [
    'DNBI accounts for 35%+ of total casualties in most operations',
    'Climate modifiers multiply base DNBI rate',
    'Acclimatization reduces heat/cold casualties by 50%',
    'Prophylaxis compliance critical in endemic disease areas'
  ],
  
  ukraine_lessons: [
    'Winter 2022-23: Significant cold weather injuries on both sides',
    'Trench warfare increased trench foot and respiratory illness',
    'Infrastructure damage created water safety issues',
    'Rasputitsa (mud season) limited operations and evacuation'
  ]
};

// ============================================================================
// ENH-045: FORCE HEALTH PROTECTION (FHP) LEVELS
// ============================================================================

/**
 * Force Health Protection levels — multiplier against base DNBI rate.
 * ENH-045: FHP_LEVEL slider (NONE/MINIMAL/STANDARD/OPTIMAL) wired into dnbiModule
 * combinedModifier. Data from FM 4-02 Force Health Protection; AJMedP-2 Preventive Medicine;
 * OEF/OIF preventive medicine outcome studies (Hyams et al. 2002; Gambel 2002).
 *
 * Planning notes:
 *  NONE     — No immunization, no water/food safety program, no medical surveillance.
 *             Expected: +25% DNBI above NATO baseline.
 *  MINIMAL  — Basic immunization series completed. No ongoing measures.
 *             Expected: +10% DNBI.
 *  STANDARD — Full immunization schedule, unit-level water/food safety, basic surveillance.
 *             NATO planning baseline. Historical deployment data calibrated to this level.
 *  OPTIMAL  — Full program: immunization, vector control, water safety, DNBI surveillance,
 *             prophylaxis compliance, heat/cold injury prevention. +25% assets required.
 *             Expected: −25% DNBI vs standard (Hyams OEF study: vaccine compliance halved GI rates).
 */
export const FHP_LEVELS = {
  NONE: {
    value: 'NONE',
    label: 'No Force Health Protection',
    shortLabel: 'NONE',
    description: 'No preventive medicine program. No immunization, water/food safety, or medical surveillance.',
    color: 'error',
    dnbi_modifier: 1.25,
    notes: 'Endemic disease, vaccine-preventable illness, and food-borne illness rates surge. High non-battle injury risk.'
  },
  MINIMAL: {
    value: 'MINIMAL',
    label: 'Minimal FHP',
    shortLabel: 'MIN',
    description: 'Basic immunization series only. No ongoing preventive medicine program.',
    color: 'warning',
    dnbi_modifier: 1.10,
    notes: 'Partial protection. Endemic disease and vector-borne illness risk remains elevated.'
  },
  STANDARD: {
    value: 'STANDARD',
    label: 'Standard FHP',
    shortLabel: 'STD',
    description: 'Full immunization schedule, basic water/food safety, unit-level medical surveillance.',
    color: 'success',
    dnbi_modifier: 1.00,
    notes: 'NATO planning baseline. Most historical deployment data calibrated to this level.'
  },
  OPTIMAL: {
    value: 'OPTIMAL',
    label: 'Optimal FHP',
    shortLabel: 'OPT',
    description: 'Full preventive medicine program: immunization, water safety, vector control, DNBI surveillance, prophylaxis.',
    color: 'info',
    dnbi_modifier: 0.75,
    notes: 'Research-based −25% DNBI reduction vs standard. Requires dedicated preventive medicine assets and compliance monitoring.'
  }
};

// ============================================================================
// DEFAULT EXPORT
// ============================================================================

// Named default export (fixes lint warning)
const climateZonesConstants = {
  CLIMATE_ZONES,
  ENDEMIC_DISEASE_LEVELS,
  WATER_FOOD_SAFETY,
  THEATER_REGIONS,
  FHP_LEVELS,
  calculateDNBIRate,
  getClimateModifiers,
  getTheaterScalar,
  validateClimateConfig,
  getClimateZoneOptions,
  getEndemicDiseaseOptions,
  DEFAULT_CLIMATE_CONFIG,
  EASTERN_EUROPE_CONFIG,
  UKRAINE_WINTER_CONFIG,
  CLIMATE_EDUCATIONAL
};

export default climateZonesConstants;


/**
 * Status of the numbers behind each option (docs/research/RESEARCH_CLIMATE.md): shown as a label next to the selector.
 * SOURCED / DERIVED / EDUCATED_GUESS / NO_DATA.
 */
export const CLIMATE_OPTION_STATUS = {
  zone: {
    TEMPERATE: 'SOURCED', TROPICAL_WET: 'DERIVED', TROPICAL_DRY: 'EDUCATED_GUESS', ARCTIC: 'SOURCED', MOUNTAINOUS: 'SOURCED',
    SUBTROPICAL: 'EDUCATED_GUESS', CARPATHIAN: 'SOURCED', EASTERN_EUROPEAN_WINTER: 'SOURCED'
  },
  endemic: 'EDUCATED_GUESS',
  water: { SAFE: 'SOURCED', CAUTION: 'EDUCATED_GUESS', UNSAFE: 'SOURCED' },
  region: 'EDUCATED_GUESS'
};
